import { ReactNode } from 'react';
import {
    HomeOutlined,
    MedicineBoxOutlined,
    SafetyOutlined,
    TeamOutlined,
    UserOutlined,
} from '@ant-design/icons';

import { ROUTES_TITLES } from '@/constants/routes.ts';
import { ROUTES_TO_ROLES_RELATION_MAP, useRoleAccess } from '@/services/roles';
import { EAdminRoutes } from '@/services/router/enums.ts';
import { checkIsPathMatch } from '@/utils/check-is-path-match.ts';

type TMenuItem = {
    key: EAdminRoutes;
    label?: string;
    icon: ReactNode;
    disabled?: boolean;
    accessible: boolean;
};

const useGetItems = (): TMenuItem[] => {
    const hasAccess = useRoleAccess();

    return [
        {
            key: EAdminRoutes.Dashboard,
            label: ROUTES_TITLES.get(EAdminRoutes.Dashboard),
            icon: <HomeOutlined />,
            accessible: hasAccess(ROUTES_TO_ROLES_RELATION_MAP.get(EAdminRoutes.Dashboard)),
        },
        {
            key: EAdminRoutes.Customers,
            label: ROUTES_TITLES.get(EAdminRoutes.Customers),
            icon: <UserOutlined />,
            accessible: hasAccess(ROUTES_TO_ROLES_RELATION_MAP.get(EAdminRoutes.Customers)),
        },
        {
            key: EAdminRoutes.Patients,
            label: ROUTES_TITLES.get(EAdminRoutes.Patients),
            icon: <TeamOutlined />,
            accessible: hasAccess(ROUTES_TO_ROLES_RELATION_MAP.get(EAdminRoutes.Patients)),
        },
        {
            key: EAdminRoutes.Practices,
            label: ROUTES_TITLES.get(EAdminRoutes.Practices),
            icon: <MedicineBoxOutlined />,
            accessible: hasAccess(ROUTES_TO_ROLES_RELATION_MAP.get(EAdminRoutes.Practices)),
        },
        {
            key: EAdminRoutes.Admins,
            label: ROUTES_TITLES.get(EAdminRoutes.Admins),
            icon: <SafetyOutlined />,
            accessible: hasAccess(ROUTES_TO_ROLES_RELATION_MAP.get(EAdminRoutes.Admins)),
        },
    ];
};

export const useNavMenu = (): [
    items: {
        key: EAdminRoutes;
        label?: string;
        icon: ReactNode;
        disabled?: boolean;
    }[],
    activeKeys: string[],
] => {
    const ITEMS = useGetItems();
    const accessibleItems = ITEMS.filter((item) => item.accessible).map((item) => ({
        ...item,
        accessible: undefined,
    }));
    const activeMenuKey = accessibleItems.find(({ key }) => checkIsPathMatch(key, false))?.key;
    const activeKeys = activeMenuKey ? [activeMenuKey] : [];

    return [accessibleItems, activeKeys];
};
